/**
 * Timeline playback clock.
 *
 * Time is ANCHORED, never accumulated: on play we record (perf, media) and
 * every frame computes media = anchorMedia + (now - anchorPerf) * rate.
 * Summing per-frame deltas drifts with rounding and dropped frames; anchoring
 * does not, and it stays correct against a real <audio> element later.
 *
 * The scheduler is injectable so the clock unit-tests in node with a fake.
 */

import type { PlaybackState } from '@canvas/shared-types';

export interface PlaybackClock {
  play(): void;
  pause(): void;
  /** → time 0, state 'stopped' */
  stop(): void;
  /** Fires one SYNCHRONOUS onTick. */
  seek(ms: number): void;
  setDuration(ms: number): void;
  setRate(rate: number): void;
  setLoop(loop: boolean): void;
  getTimeMs(): number;
  getState(): PlaybackState;
  destroy(): void;
}

export interface ClockScheduler {
  now(): number;
  raf(cb: (t: number) => void): number;
  cancel(id: number): void;
}

/** A single rAF gap longer than this is a throttled/backgrounded tab, not playback. */
export const THROTTLE_GAP_MS = 500;

const defaultScheduler: ClockScheduler = {
  now: () => performance.now(),
  raf: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
};

/** Create a clock. It starts stopped at 0. */
export function createPlaybackClock(opts: {
  durationMs: number;
  loop?: boolean;
  rate?: number;
  onTick: (ms: number) => void;
  onEnd?: () => void;
  onStateChange?: (s: PlaybackState) => void;
  /** Injected for unit tests; defaults to performance/requestAnimationFrame. */
  scheduler?: ClockScheduler;
}): PlaybackClock {
  const sched = opts.scheduler || defaultScheduler;
  let durationMs = Math.max(0, opts.durationMs);
  let loop = Boolean(opts.loop);
  let rate = opts.rate && opts.rate > 0 ? opts.rate : 1;
  let state: PlaybackState = 'stopped';
  let timeMs = 0;
  let anchorPerf = 0;
  let anchorMedia = 0;
  let lastFramePerf = 0;
  let rafId: number | null = null;
  let destroyed = false;

  const clampTime = (ms: number): number => Math.min(durationMs, Math.max(0, ms));

  const setState = (next: PlaybackState): void => {
    if (state === next) return;
    state = next;
    opts.onStateChange?.(next);
  };

  const anchor = (perf: number): void => {
    anchorPerf = perf;
    anchorMedia = timeMs;
    lastFramePerf = perf;
  };

  const cancelFrame = (): void => {
    if (rafId !== null) sched.cancel(rafId);
    rafId = null;
  };

  const frame = (): void => {
    rafId = null;
    if (destroyed || state !== 'playing') return;
    const perf = sched.now();

    // A backgrounded tab resuming must not teleport the playhead.
    if (perf - lastFramePerf > THROTTLE_GAP_MS) anchor(perf);
    lastFramePerf = perf;

    const raw = anchorMedia + (perf - anchorPerf) * rate;
    if (raw >= durationMs) {
      if (loop && durationMs > 0) {
        timeMs = raw % durationMs;
        anchor(perf);
      } else {
        timeMs = durationMs;
        opts.onTick(timeMs);
        setState('paused');
        opts.onEnd?.();
        return;
      }
    } else {
      timeMs = raw;
    }
    opts.onTick(clampTime(timeMs));
    rafId = sched.raf(frame);
  };

  return {
    play() {
      if (destroyed || state === 'playing' || durationMs <= 0) return;
      // Pressing play at the end restarts rather than doing nothing.
      if (timeMs >= durationMs) timeMs = 0;
      anchor(sched.now());
      setState('playing');
      cancelFrame();
      rafId = sched.raf(frame);
    },
    pause() {
      if (destroyed) return;
      cancelFrame();
      if (state === 'playing') setState('paused');
      opts.onTick(clampTime(timeMs));
    },
    stop() {
      if (destroyed) return;
      cancelFrame();
      timeMs = 0;
      setState('stopped');
      opts.onTick(0);
    },
    seek(ms) {
      if (destroyed) return;
      timeMs = clampTime(ms);
      if (state === 'playing') anchor(sched.now());
      else if (state === 'stopped' && timeMs > 0) setState('paused');
      opts.onTick(timeMs);
    },
    setDuration(ms) {
      durationMs = Math.max(0, ms);
      if (timeMs > durationMs) timeMs = durationMs;
    },
    setRate(next) {
      if (!(next > 0)) return;
      // Re-anchor first so time already elapsed keeps the old rate.
      if (state === 'playing') {
        timeMs = clampTime(anchorMedia + (sched.now() - anchorPerf) * rate);
        anchor(sched.now());
      }
      rate = next;
    },
    setLoop(next) {
      loop = next;
    },
    getTimeMs() {
      return clampTime(timeMs);
    },
    getState() {
      return state;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelFrame();
    },
  };
}
